import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/Button';
import warningIcon from '@/assets/warning.svg';
import { formatCurrency } from '@/utils/formatUtils';
import { getRevenuePreview } from '@/api/listing';

const PriceEstimateSection = ({
  price,
  quantity,
  type,
  productId,
  boxType,
  onSubmit,
  isLoading,
  isEditMode,
  onCancel,
  isFormValid = true
}) => {
  const { t } = useTranslation();

  // State for API-based price estimation
  const [revenueData, setRevenueData] = useState(null);
  const [isLoadingRevenue, setIsLoadingRevenue] = useState(false);

  // Debounced API call for revenue preview
  const debouncedGetRevenuePreview = useCallback(
    (() => {
      let timeoutId;
      return (productId, price, type) => {
        clearTimeout(timeoutId);

        // Show loading immediately if we have valid inputs and price >= 1000
        if (price >= 1000 && productId && type) {
          setIsLoadingRevenue(true);
        } else {
          setIsLoadingRevenue(false);
          setRevenueData(null);
          return;
        }

        timeoutId = setTimeout(async () => {
          try {
            const response = await getRevenuePreview({
              productId,
              price,
              type
            });
            setRevenueData(response.data);
          } catch (error) {
            console.error('Error fetching revenue preview:', error);
            // Clear data if API fails - no frontend calculations
            setRevenueData(null);
          } finally {
            setIsLoadingRevenue(false);
          }
        }, 1000); // 1 second debounce
      };
    })(),
    []
  );

  // Effect to call API when price, productId, or type changes
  useEffect(() => {
    debouncedGetRevenuePreview(productId, price, type);
  }, [price, productId, type, debouncedGetRevenuePreview]);

  // Use API data only - no frontend calculations
  const serviceFee = revenueData?.serviceFee || 0;
  const revenue = revenueData?.revenue || 0;

  // Calculate income per box for CARD_BOX type (only if we have API data)
  const incomePerBox = type === 'CARD_BOX' && quantity > 0 && revenueData ? Math.floor(revenue / quantity) : 0;

  // Helper function to render value with loading state
  const renderValue = (value, isLoading = false) => {
    if (isLoading) {
      return (
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
        </div>
      );
    }
    if (!revenueData && price > 0) {
      return '−'; // En dash for better UX
    }
    return formatCurrency(value);
  };

  return (
    <div className="bg-white rounded-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-[#111111] text-lg font-bold">
          {t('sell.price_estimate.title')}
        </h2>
        <a
          href="/terms/guideline_listing"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-[#2966ff] text-sm font-medium hover:text-[#1e4fd6] transition-colors cursor-pointer"
        >
          <img src={warningIcon} alt="Warning" className="w-5 h-5 mr-1" />
          {t('sell.price_estimate.rules')}
        </a>
      </div>
      
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <span className="text-[#111111] text-sm font-normal">
            {t('sell.price_estimate.price')}
          </span>
          <span className="text-[#111111] text-sm font-bold">
            {formatCurrency(price)}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-[#666666] text-sm font-normal">
            {t('sell.price_estimate.tech_fee')}
          </span>
          <span className="text-[#666666] text-sm font-medium">
            {!isLoadingRevenue && '-'} {renderValue(serviceFee, isLoadingRevenue)}
          </span>
        </div>

        <div className="h-px bg-[#111111] my-2"></div>

        <div className="flex justify-between items-center">
          <span className="text-[#111111] text-lg font-bold">
            {t('sell.price_estimate.total_income')}
          </span>
          <span className="text-[#111111] text-xl font-bold">
            {renderValue(revenue, isLoadingRevenue)}
          </span>
        </div>

        {type === 'CARD_BOX' && quantity > 1 && (revenueData || isLoadingRevenue) && (
          <div className="flex justify-between items-center !mt-1">
            <span className="text-[#111111] text-md font-normal" />
            <span className="text-[#111111] text-md">
              {isLoadingRevenue ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                </div>
              ) : (
                `${formatCurrency(incomePerBox)}/${boxType === 'PACK' ? t('box.pack') : t('box.box_unit')}`
              )}
            </span>
          </div>
        )}

        {isEditMode ? (
          <div className="flex gap-3 mt-8">
            <Button
              variant="default"
              size="xl"
              className="flex-1 py-4 text-xl font-medium text-[#FF2B00]"
              onClick={onCancel}
            >
              {t('sell.cancel_listing')}
            </Button>
            <Button
              variant="primary"
              size="xl"
              className="flex-1 py-4 text-xl font-medium"
              disabled={isLoading || !isFormValid}
              onClick={onSubmit}
            >
              {t('sell.repost_listing')}
            </Button>
          </div>
        ) : (
          <>
            <Button
              variant="primary"
              size="xl"
              className="w-full mt-8 py-4 text-xl font-medium"
              disabled={isLoading || !isFormValid}
              onClick={onSubmit}
            >
              {isLoading ? t('common.loading') : t('sell.list_button')}
            </Button>
            <div className="mt-4 text-xs text-gray-500 leading-relaxed whitespace-pre-line">
              {t('sell.price_estimate.return_policy_tip')}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PriceEstimateSection; 